"use client";
import useTranslate from "@app/_hooks/useTranslate";

import type { NavigationLink } from "@components/header/types";
import { useHeaderContext } from "@context/headerContext";
import type { NavigationDirection } from "@context/mobileMenuContext";
import { MobileMenuContext } from "@context/mobileMenuContext";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import { useMenuStore } from "@/stores/menuStore";
import { parseParams } from "@/utils/params";

import MenuIcon from "@app/_icons/MenuIcon/MenuIcon";
import { Button } from "@ui/button";
import { Dialog, DialogClose, DialogContent, DialogHeader } from "@ui/dialog";
import { getInitialNavigationTree } from "./MobileMenu.hooks";
import MobileMenuBottom from "./MobileMenuBottom";
import MobileMenuHeader from "./MobileMenuHeader";
import MobileMenuLinks from "./MobileMenuLinks";
import MobileMenuTop from "./MobileMenuTop";

export default function MobileMenu() {
  const t = useTranslate();
  const ref = useRef<HTMLDivElement>(null);
  const { navigation: links } = useHeaderContext();
  const pathname = usePathname();
  const { slug } = parseParams({ pathname });
  const mobileMenuOpen = useMenuStore((state) => state.mobileMenuOpen);
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);
  const [navigationTree, setNavigationTree] = useState<NavigationLink[]>(
    getInitialNavigationTree({ slug, links }),
  );
  const [navigationDirection, setNavigationDirection] =
    useState<NavigationDirection>("right");

  const context = useMemo(
    () => ({
      navigationTree,
      setNavigationTree,
      navigationDirection,
      setNavigationDirection,
      mobileMenuOpen,
      setMobileMenuOpen,
    }),
    [
      navigationTree,
      setNavigationTree,
      navigationDirection,
      setNavigationDirection,
      mobileMenuOpen,
      setMobileMenuOpen,
    ],
  );

  useEffect(() => {
    ref.current?.scrollTo({ top: 0 });
  }, [navigationTree]);

  return (
    <MobileMenuContext.Provider value={context}>
      <Button
        onClick={() => setMobileMenuOpen(true)}
        className="p-1 lg:hidden"
        variant="ghost"
      >
        <MenuIcon className="size-4" />
        <span className="sr-only">{t("menu")}</span>
      </Button>
      <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <DialogContent
          ref={ref}
          className="flex h-full max-w-xs grow flex-col content-stretch overflow-auto text-base"
          position="top-left"
          animate="from-left"
        >
          <DialogHeader className="sticky top-0 z-20 min-h-0 shrink-0 border-b">
            <MobileMenuHeader />
            <DialogClose className="all-small-caps w-auto text-base tracking-wider uppercase underline">
              {t("close")}
            </DialogClose>
          </DialogHeader>
          <MobileMenuTop />
          <MobileMenuLinks />
          <MobileMenuBottom />
        </DialogContent>
      </Dialog>
    </MobileMenuContext.Provider>
  );
}
